import type { DownloadedFile } from '@brush/api-client';

/** The pieces of the browser `saveFile` touches, injected so `node --test` can record what it does and in what order. */
export interface SaveEnvironment {
  document: { createElement(tag: 'a'): HTMLAnchorElement; body: { appendChild(node: HTMLAnchorElement): unknown } };
  url: { createObjectURL(blob: Blob): string; revokeObjectURL(url: string): void };
  /** When the object URL is revoked. A tick after the click: some browsers lose the file if it is revoked in the same one. */
  defer(run: () => void): void;
}

const browser = (): SaveEnvironment => ({
  document,
  url: URL,
  defer: (run) => window.setTimeout(run, 0),
});

/**
 * EX25's last step: the Blob becomes an object URL, a temporary `<a download>` carrying the server's file name is
 * clicked, removed, and the URL revoked — in that order, which is the rule: revoking before the click saves nothing,
 * and a link left in the page or a URL never revoked holds the file's bytes for the life of the tab. The link points at
 * the Blob and never at the API, which an `<a href>` could not reach with the token.
 */
export function saveFile(file: DownloadedFile, env: SaveEnvironment = browser()): void {
  const href = env.url.createObjectURL(file.blob);
  const anchor = env.document.createElement('a');
  anchor.href = href;
  anchor.download = file.fileName;
  anchor.style.display = 'none';
  env.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  env.defer(() => env.url.revokeObjectURL(href));
}
